import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './CloudStaffDashboard.css';
import cloudsafeLogo from '../../../assets/cloudsafe-logo.png';
import CloudStaffSidebar from './CloudStaffSidebar';
import { FaArrowLeft, FaUserCircle, FaSignOutAlt, FaServer, FaUserPlus, FaCheck, FaBars } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getUserRole } from '../../../utils/roleUtils';
import { clearToken } from '../../../utils/auth';
import { getApiEndpoint } from '../../../config/api';

const ROLE_OPTIONS = [
  { value: 'viewer', label: 'Viewer', desc: 'Read-only property access' },
  { value: 'staff', label: 'Staff / FM', desc: 'Facility management, can manage properties and contractors' },
  { value: 'admin', label: 'Admin', desc: 'Property admin with full access' },
  { value: 'fm', label: 'FM (Facility Manager)', desc: 'Facility manager role' },
  { value: 'contractor', label: 'Contractor / Engineer', desc: 'Contractor company admin or on-site engineer' },
  { value: 'building_manager', label: 'Building Manager', desc: 'On-site building manager' },
  { value: 'property-onboarding-staff', label: 'Property Onboarding Staff', desc: 'Onboarding and setup' },
];

const CloudStaffAccountCreation = () => {
  const navigate = useNavigate();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [userName, setUserName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [createdUser, setCreatedUser] = useState(null);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    role: 'viewer',
    accountType: '',
    companyName: '',
    isCompanyAdmin: false,
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const userRole = getUserRole();
    if (userRole !== 'cloud_staff' && userRole !== 'super_admin') {
      toast.error('Access denied');
      navigate('/login');
      return;
    }
    loadProfile();
  }, [navigate]);

  const loadProfile = async () => {
    try {
      const res = await fetch(getApiEndpoint('/api/users/profile'), {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUserName(`${data.firstName || data.first_name || ''} ${data.lastName || data.last_name || ''}`);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleLogout = () => {
    clearToken();
    localStorage.removeItem('userRole');
    localStorage.removeItem('token');
    navigate('/login');
  };

  const validate = () => {
    const e = {};
    if (!form.firstName?.trim()) e.firstName = 'First name is required';
    if (!form.lastName?.trim()) e.lastName = 'Last name is required';
    if (!form.email?.trim()) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email';
    if (!form.password) e.password = 'Password is required';
    else if (form.password.length < 8) e.password = 'Password must be at least 8 characters';
    else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])/.test(form.password)) {
      e.password = 'Include upper, lower, number and special character';
    }
    if (form.password !== form.confirmPassword) e.confirmPassword = 'Passwords do not match';
    const needsCompany = (form.role === 'contractor' && form.isCompanyAdmin) || form.role === 'fm';
    if (needsCompany && !form.companyName?.trim()) e.companyName = 'Company name is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error('Please fix the errors below');
      return;
    }
    setSubmitting(true);
    setErrors({});
    try {
      const res = await fetch(getApiEndpoint('/api/cloud-staff/users'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim().toLowerCase(),
          password: form.password,
          phoneNumber: form.phoneNumber?.trim() || null,
          role: form.role,
          accountType: form.role === 'contractor' ? (form.isCompanyAdmin ? 'contractor_admin' : 'contractor_staff') : form.role === 'fm' ? 'fm' : form.role === 'building_manager' ? 'building_manager' : null,
          companyName: form.companyName?.trim() || null,
          isCompanyAdmin: form.role === 'contractor' ? form.isCompanyAdmin : (form.role === 'fm' || form.role === 'staff'),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `Request failed (${res.status})`);
      setCreatedUser(data.user);
      setForm({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        password: '',
        confirmPassword: '',
        role: 'viewer',
        accountType: '',
        companyName: '',
        isCompanyAdmin: false,
      });
      toast.success('Account created successfully');
    } catch (err) {
      toast.error(err.message || 'Failed to create account');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const needsCompanyName = (form.role === 'contractor' && form.isCompanyAdmin) || form.role === 'fm' || form.role === 'staff';
  const showContractorSubRole = form.role === 'contractor';

  return (
    <div className="cs-dashboard">
      <header className="cs-header">
        <div className="cs-header-left">
          <button className="cs-mobile-menu-btn" onClick={() => setMobileSidebarOpen(true)}><FaBars /></button>
          <Link to="/cloud-staff/dashboard">
            <img src={cloudsafeLogo} alt="CloudSafe" className="cs-logo" />
          </Link>
          <span className="cs-header-badge">Cloud Staff</span>
        </div>
        <div className="cs-header-right">
          <div className="cs-header-icons">
            <Link to="/cloud-staff/dashboard" className="cs-icon-btn" title="Dashboard">
              <FaServer />
            </Link>
            <button
              type="button"
              className="cs-user-avatar"
              onClick={() => setShowUserMenu(!showUserMenu)}
              aria-label="User menu"
            >
              <FaUserCircle />
            </button>
            {showUserMenu && (
              <div className="cs-user-menu">
                <div className="cs-user-menu-header">
                  <FaUserCircle className="cs-menu-avatar" />
                  <span className="cs-menu-name">{userName || 'User'}</span>
                </div>
                <button type="button" className="cs-menu-item" onClick={handleLogout}>
                  <FaSignOutAlt /> Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="cs-layout">
        <CloudStaffSidebar active="account-creation" mobileOpen={mobileSidebarOpen} onClose={() => setMobileSidebarOpen(false)} />
        <main className="cs-content">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              marginBottom: '1.5rem',
              paddingBottom: '1.5rem',
              borderBottom: '1px solid #21262d'
            }}
          >
            <Link
              to="/cloud-staff/dashboard"
              style={{
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px',
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid #30363d',
                color: '#8b949e',
                textDecoration: 'none',
                transition: 'all 0.2s'
              }}
            >
              <FaArrowLeft />
            </Link>
            <div>
              <h1 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#f0f6fc', margin: 0 }}>
                Account Creation
              </h1>
              <p style={{ fontSize: '0.85rem', color: '#8b949e', margin: 0 }}>
                Create new user accounts and assign roles. Link them to companies in Company Generation.
              </p>
            </div>
          </div>

          {createdUser && (
            <div
              style={{
                marginBottom: '1.5rem',
                padding: '1rem 1.25rem',
                background: 'rgba(35, 134, 54, 0.15)',
                border: '1px solid #238636',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem'
              }}
            >
              <FaCheck style={{ color: '#3fb950', fontSize: '1.25rem' }} />
              <div>
                <div style={{ fontWeight: 600, color: '#e1e4e8' }}>
                  {createdUser.firstName} {createdUser.lastName} ({createdUser.email}) created.
                </div>
                <div style={{ fontSize: '0.8rem', color: '#8b949e', marginTop: '0.25rem' }}>
                  Role: {createdUser.role}
                  {createdUser.userCode && ` · CloudSafe ID: ${createdUser.userCode}`}
                  {createdUser.companyCode && ` · Company ID: ${createdUser.companyCode}`}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setCreatedUser(null)}
                style={{
                  marginLeft: 'auto',
                  padding: '0.35rem 0.75rem',
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid #30363d',
                  borderRadius: '6px',
                  color: '#8b949e',
                  fontSize: '0.8rem',
                  cursor: 'pointer'
                }}
              >
                Dismiss
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ maxWidth: '520px' }}>
            <div className="cs-form-group" style={{ marginBottom: '1rem' }}>
              <label>Role *</label>
              <select
                className="cs-select"
                value={form.role}
                onChange={(e) => handleChange('role', e.target.value)}
              >
                {ROLE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label} – {opt.desc}
                  </option>
                ))}
              </select>
            </div>

            {showContractorSubRole && (
              <div className="cs-form-group" style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={form.isCompanyAdmin}
                    onChange={(e) => handleChange('isCompanyAdmin', e.target.checked)}
                  />
                  Company Admin (owns contractor company)
                </label>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="cs-form-group">
                <label>First name *</label>
                <input
                  type="text"
                  className="cs-input"
                  value={form.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  placeholder="First name"
                />
                {errors.firstName && <span style={{ fontSize: '0.8rem', color: '#f85149' }}>{errors.firstName}</span>}
              </div>
              <div className="cs-form-group">
                <label>Last name *</label>
                <input
                  type="text"
                  className="cs-input"
                  value={form.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  placeholder="Last name"
                />
                {errors.lastName && <span style={{ fontSize: '0.8rem', color: '#f85149' }}>{errors.lastName}</span>}
              </div>
            </div>

            <div className="cs-form-group" style={{ marginTop: '1rem' }}>
              <label>Email *</label>
              <input
                type="email"
                className="cs-input"
                value={form.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="user@example.com"
              />
              {errors.email && <span style={{ fontSize: '0.8rem', color: '#f85149' }}>{errors.email}</span>}
            </div>

            <div className="cs-form-group">
              <label>Phone</label>
              <input
                type="tel"
                className="cs-input"
                value={form.phoneNumber}
                onChange={(e) => handleChange('phoneNumber', e.target.value)}
                placeholder="+44 7xxx xxxxxx"
              />
            </div>

            {needsCompanyName && (
              <div className="cs-form-group">
                <label>Company name *</label>
                <input
                  type="text"
                  className="cs-input"
                  value={form.companyName}
                  onChange={(e) => handleChange('companyName', e.target.value)}
                  placeholder="Company name"
                />
                {errors.companyName && <span style={{ fontSize: '0.8rem', color: '#f85149' }}>{errors.companyName}</span>}
              </div>
            )}

            <div className="cs-form-group">
              <label>Password *</label>
              <input
                type="password"
                className="cs-input"
                value={form.password}
                onChange={(e) => handleChange('password', e.target.value)}
                placeholder="Min 8 chars, upper, lower, number, special"
                autoComplete="new-password"
              />
              {errors.password && <span style={{ fontSize: '0.8rem', color: '#f85149' }}>{errors.password}</span>}
            </div>

            <div className="cs-form-group">
              <label>Confirm password *</label>
              <input
                type="password"
                className="cs-input"
                value={form.confirmPassword}
                onChange={(e) => handleChange('confirmPassword', e.target.value)}
                placeholder="Confirm password"
                autoComplete="new-password"
              />
              {errors.confirmPassword && <span style={{ fontSize: '0.8rem', color: '#f85149' }}>{errors.confirmPassword}</span>}
            </div>

            <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.75rem' }}>
              <button type="submit" className="cs-primary-btn" disabled={submitting}>
                {submitting ? 'Creating...' : 'Create account'}
              </button>
              <button
                type="button"
                style={{
                  padding: '0.65rem 1.25rem',
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid #30363d',
                  borderRadius: '8px',
                  color: '#8b949e',
                  fontSize: '0.85rem',
                  cursor: 'pointer'
                }}
                onClick={() => setForm({ firstName: '', lastName: '', email: '', phoneNumber: '', password: '', confirmPassword: '', role: 'viewer', accountType: '', companyName: '', isCompanyAdmin: false })}
              >
                Clear
              </button>
            </div>
          </form>
        </main>
      </div>
    </div>
  );
};

export default CloudStaffAccountCreation;
